import { Component, Element, h, Host, Listen, Method, Prop, State, Watch, Event, EventEmitter } from "@stencil/core";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import {
  VideoplayerLocalization,
  VideoPlayerOrientation,
  VideoplayerProgressEventArgs,
  VideoplayerSchemeDefinition,
  VideoplayerSchemeName,
  VideoPlayerType
} from "./constants/videoplayer-types";
import {
  VIDEOPLAYER_ORIENTATION_LANDSCAPE,
  VIDEOPLAYER_SCHEME_CONFIG,
  VIDEOPLAYER_SCHEME_MAP,
  VIDEOPLAYER_TYPE_FILE,
  VIDEOPLAYER_TYPE_MOVING_IMAGE,
  VIDEOPLAYER_TYPE_YOUTUBE
} from "./constants/videoplayer-constants";
import { KeyIds } from "../../constants/keys";
import screenfull from "screenfull";
import { isMobile } from "../../utils/screen-helper";
import { uniqueId } from "../../utils/unique-id";
import { isIphoneOrIpod, isSafari } from "../../utils/device-helper";
import { LocalizableComponent, Localization, setCurrentLocalizationDelegate } from "../../common/localizable-component";
import { HEADLINE_TAG_H3 } from "../headline/constants/headline-constants";
import pfx from "../../utils/style-class-prefix-helper";
import { getFirstNestedChild } from "../../utils/dom-helper";
import { VIDEOPLAYER_LOCALIZATION } from "./constants/videoplayer-i18n";
import { HeadlineTag } from "../headline/constants/headline-types";
import { useTheRightHtmlTagForHeadline } from "../../utils/headline-helper";
import {
  HEADLINE_ALIGNMENT_LEFT,
  VIDEOPLAYER_BUTTON_SCHEME,
  VIDEOPLAYER_FILLED_ICON
} from "../../constants/global-constants";
import { VideoplayerSchemeConfiguration } from "../../constants/colors";
import { isTouchDevice } from "../../utils/touch-helper";
import { BackgroundColor } from "../background/constants/background-types";
import { StyledComponent } from "../../decorators/styled-component";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot image - You can put an <eon-ui-image> component in that slot for Poster-Image of VideoPlayer.
 */

@Component({
  tag: "eon-ui-videoplayer",
  styleUrl: "videoplayer.scss",
  assetsDirs: ["assets-videoplayer"],
  shadow: true
})
export class Videoplayer
  implements
    ColorableComponent<VideoplayerSchemeConfiguration<VideoplayerSchemeDefinition>>,
    LocalizableComponent<VideoplayerLocalization> {
  // @InitLogging() sentryTransaction;
  schemeConfig: VideoplayerSchemeConfiguration<VideoplayerSchemeDefinition> = VIDEOPLAYER_SCHEME_CONFIG;
  schemeMap = VIDEOPLAYER_SCHEME_MAP;

  localization: Localization<VideoplayerLocalization> = VIDEOPLAYER_LOCALIZATION;
  currentLocalization: VideoplayerLocalization;

  private internalId: string;
  private videoplayerElement: HTMLDivElement;
  private cacheBackgroundVideoProps: object = {};
  private headlineComponent: HTMLEonUiHeadlineElement;
  private videoButtonDescriptionId: string = uniqueId();
  /**
   * The components active scheme. If {@link scheme} is set, it will be set to the corresponding scheme.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @State() activeScheme: VideoplayerSchemeDefinition;

  @State() isPlaying: boolean;
  @State() isMuted: boolean;
  @State() isFullscreen: boolean;
  @State() duration: number = 0;
  @State() seekTime: number = 0;
  @State() secondsPlayed: number = 0;
  @State() percentPlayed: number = 0;
  @State() isPausedOutOfViewport: boolean;
  @State() hasError: boolean;
  @State() isStopped: boolean;
  @State() hasFinishedPlaying: boolean;
  @State() isIosMobileDevice: boolean;
  @State() isMouseOver: boolean;
  @State() posterImagePlaceholder: boolean;
  @State() seoHeadlineTag: HeadlineTag;

  /**
   * Aria-Label for accessibility
   */
  @Prop({ attribute: "a11y-aria-label", mutable: true, reflect: true }) a11yAriaLabel: string;

  /**
   * Can be set to provide further information about the video, e.g. if it's appearance is bright or dark.
   */
  @Prop() appearance: "bright" | "dark";

  /**
   * The source URL of the video | Video ID Youtube.
   */
  @Prop() src: string;

  /**
   * The source URL of the video captions (for file video playback)
   */
  @Prop() track: string;

  /**
   * Display controls permanently below in windowed mode
   */
  @Prop() controlsBelow: boolean = true;

  /**
   * Set to true to hide the control bar of the videoplayer.
   */
  @Prop({ mutable: true }) hideControls: boolean;

  /**
   * Set to true to hide the Mute button in the control bar of the videoplayer.
   */
  @Prop() hideMuteButton: boolean;

  /**
   * Set to true if the player should attempt to autoplay the video.
   * This doesn't work on iPhones and requires the video to be muted as well.
   */

  @Prop({ mutable: true }) autoplay: boolean = false;

  /**
   * A headline displayed on top of the poster-image.
   */
  @Prop() headline?: string;

  /**
   * this property is used for SEO-Optimisation.<br>
   *   If this property is used and fill-out, this property is used for HTML-Tag for headline.
   */
  @Prop() headlineHtmlTag: HeadlineTag;

  /**
   * Whether or not the headline text should have a text shadow. Use this if you need more contrast between poster-image and headline.
   */
  @Prop() headlineTextShadow: boolean;

  /**
   * Set this to true if the video will be used as a full size background video of the parent div.
   */
  @Prop({ reflect: true }) useAsBackground: boolean = false;

  /**
   * Select the orientation of the video player.<br>
   * Landscape format (16:9) or portrait format (9:16)
   */
  @Prop() orientation: VideoPlayerOrientation = VIDEOPLAYER_ORIENTATION_LANDSCAPE;

  /**
   * The src to an image displayed as poster-image before the video has started and after it has ended.
   * poster-image property shows or hides the image on video player depending on the value
   */
  @Prop({ mutable: true }) posterImage?: boolean;

  /**
   * Set to false to hide the error message layer when video can't play
   */
  @Prop({ mutable: true }) showErrorMessage: boolean = true;

  /**
   * Flexibility to editor for choosing player: flie player or youtube?
   */
  @Prop() type: VideoPlayerType = VIDEOPLAYER_TYPE_FILE;

  @StyledComponent() component: any;
  @Element() hostElement: HTMLEonUiVideoplayerElement;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: VideoplayerSchemeName;

  /**
   * noCookie property will embed nocookie term into youTube video url
   * which prevent youtube from tracking user's viewing behaviour,
   * hence no activity is collected to personalise the viewing experience.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true }) noCookie: boolean;

  /**
   * This property used for darkening the video.
   */
  @Prop() shadowOnVideo: boolean = false;

  /**
   * captions property sets Caption/Subtitles on Video
   */
  @Prop() captions: boolean = true;

  /**
   * Caption language Code can be set with languageCode property
   * Youtube: As per ISO 639-1 two-letter language code: https://www.loc.gov/standards/iso639-2/php/code_list.php
   */
  @Prop() languageCode: string;

  /**
   * Raised when the videoplayer has been loaded.
   */
  @Event() videoplayerLoad: EventEmitter;

  /**
   * Emitted when user interacts with player. The event detail contains the new status, e.g. "play", "pause", "mute", ...
   */
  @Event() videoplayerInteract: EventEmitter;

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  componentWillLoad() {
    this.applyScheme();
    this.setCurrentLocalization();

    this.internalId = uniqueId();
    this.isStopped = true;
    this.isIosMobileDevice = isIphoneOrIpod(); // Videos are launched in the native player on iOS mobile devices. The player also controls the fullscreen mode.

    this.prepareAutoplay();

    if (!this.useAsBackground && screenfull.isEnabled) {
      screenfull.on("change", () => this.handleFullscreenChange());
    }
    this.EnterViewport(this.hostElement);
    this.imageAsPoster();
    this.handleHeadlineTag();
    this.prepareBackgroundVideo();
  }

  componentDidLoad() {
    this.setCssVariables();
    this.videoplayerLoad.emit();
    this.applyHeadlineScheme();
  }

  disconnectedCallback() {
    if (screenfull.isEnabled) {
      screenfull.off("change", () => this.handleFullscreenChange());
    }
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("autoplay")
  prepareAutoplay(): void {
    if (this.autoplay) {
      this.isMuted = true;
      this.isPlaying = true;
    }
  }

  @Watch("headlineHtmlTag")
  handleHeadlineTag() {
    this.seoHeadlineTag = useTheRightHtmlTagForHeadline(this.headlineHtmlTag, HEADLINE_TAG_H3);
  }

  @Watch("useAsBackground")
  prepareBackgroundVideo(): void {
    if (this.useAsBackground === true) {
      // create a cache to store property settings
      this.cacheBackgroundVideoProps = {
        showErrorMessage: this.showErrorMessage,
        hideControls: this.hideControls,
        posterImage: this.posterImage,
        autoplay: this.autoplay
      };

      this.showErrorMessage = false;
      this.hideControls = true;
      this.posterImage = true;
      this.autoplay = true;
      this.EnterViewport(this.hostElement);
    } else if (Object.keys(this.cacheBackgroundVideoProps).length) {
      // recreate property settings if cached
      this.showErrorMessage = this.cacheBackgroundVideoProps["showErrorMessage"];
      this.hideControls = this.cacheBackgroundVideoProps["hideControls"];
      this.posterImage = this.cacheBackgroundVideoProps["posterImage"];
      this.autoplay = this.cacheBackgroundVideoProps["autoplay"];
      // after recreate of the property settings clear entries from cache
      for (var member in this.cacheBackgroundVideoProps) delete this.cacheBackgroundVideoProps[member];
    }
  }

  play() {
    !this.isPlaying && this.videoplayerInteract.emit("play"); // Check for isPlaying as player event is emitted too often
    this.isPlaying = true;
    this.isStopped = false;

    // this.videoplayerElement?.focus();

    const setActivePlayerEvent = new CustomEvent("activePlayerChange", {
      detail: this.internalId,
      bubbles: true
    });

    document.dispatchEvent(setActivePlayerEvent);

    if (
      isMobile() &&
      this.isIosMobileDevice &&
      isTouchDevice() &&
      !this.useAsBackground &&
      !this.isPausedOutOfViewport
    ) {
      this.enterFullscreen();
    }
  }

  pause = () => {
    this.isPlaying && this.videoplayerInteract.emit("pause"); // Check for isPlaying as player event is emitted too often
    this.isPlaying = false;
  };

  stop = () => {
    if (this.isFullscreen) {
      this.exitFullscreen();
    }

    this.hasError = false;
    this.hasFinishedPlaying = true;
    this.isPlaying = false;
    this.secondsPlayed = 0;
    this.percentPlayed = 0;
    this.seekTime = 0;
    this.isStopped = true;
    this.videoplayerInteract.emit("stop");
  };

  // Note that this will call handleFullscreenChange indirectly.
  // State is updated there, so we don't need to do it here.
  enterFullscreen = () => {
    if (screenfull.isEnabled) {
      screenfull.request(this.videoplayerElement);
      this.videoplayerInteract.emit("enterFullscreen");
    }
  };

  exitFullscreen = () => {
    if (screenfull.isEnabled) {
      screenfull.exit();
      this.videoplayerInteract.emit("exitFullscreen");
    }
  };

  /**
   * Method to toggle the video-sound, e.g. videoElement.toggleSound();
   */
  @Method()
  async toggleSound(): Promise<void> {
    this.handleMuteToggle();
  }

  /**
   * Method to toggle captions display, e.g. videoElement.toggleCaptions();
   */
  @Method()
  async toggleCaptions(): Promise<void> {
    this.handleToggleCaptions();
  }

  /**
   * Method to play the video, e.g. videoElement.startPlayback();
   */
  @Method()
  async startPlayback(): Promise<void> {
    this.play();
  }

  /**
   * Method to stop the video, e.g. videoElement.stopPlayback();
   */
  @Method()
  async stopPlayback(): Promise<void> {
    this.stop();
  }

  /**
   * Method to pause the video, e.g. videoElement.pausePlayback();
   */
  @Method()
  async pausePlayback(): Promise<void> {
    this.pause();
  }

  @Listen("activePlayerChange", { target: "document" })
  handleActivePlayerChange(event: Event) {
    // another player started playing
    if (!this.autoplay && this.isPlaying && (event as any).detail !== this.internalId) {
      this.pause();
    }
  }

  @Listen("canPlayEvent")
  handleCanPlay(event: Event) {
    const duration: number = event["detail"];
    if (duration === 0) {
      this.handleError();
    }
    this.duration = duration;
  }

  @Listen("endedEvent")
  handleEnded() {
    this.stop();
  }

  @Listen("errorEvent")
  handleError() {
    this.hasError = true;
    this.hasFinishedPlaying = false;
    this.isPlaying = false;
    this.isStopped = true;
  }

  @Listen("playToggleEvent")
  private handlePlayToggle() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  @Listen("muteToggleEvent")
  handleMuteToggle() {
    this.isMuted = !this.isMuted;
    this.videoplayerInteract.emit(`${this.isMuted ? "mute" : "unmute"}`);
  }

  @Listen("mouseenter")
  handleMouseEnter() {
    this.isMouseOver = true;
  }

  @Listen("mouseleave")
  handleMouseLeave() {
    this.isMouseOver = false;
  }

  // This is a document-wide listener called by the browser when fullscreen mode is entered/exited.
  // Note that ALL instances of the VideoPlayer listen to that event.
  // So please don't do anything instance-specific here like pausing the video.
  @Listen("nativePlayerFullscreenChangeEvent")
  handleFullscreenChange() {
    // see https://github.com/sindresorhus/screenfull.js/issues/173
    this.isFullscreen = (screenfull as any).isFullscreen;
  }

  @Listen("fullscreenToggleEvent")
  handleFullScreenToggle() {
    if (this.isFullscreen) {
      this.exitFullscreen();
    } else {
      this.enterFullscreen();
    }
  }

  @Listen("playEvent")
  handlePlay() {
    this.play();
  }

  @Listen("pauseEvent")
  handlePause() {
    this.pause();
  }

  @Listen("seekEvent")
  handleSeek(event: Event) {
    const args: VideoplayerProgressEventArgs = event["detail"];
    this.seekTime = args.seconds;
    this.secondsPlayed = args.seconds;
    this.percentPlayed = args.percent;
    this.videoplayerInteract.emit("seek");
  }

  @Listen("keyboardSeekEvent")
  handleKeyboardSeek(event: Event) {
    this.keyboardSeek(event["detail"]);
  }

  @Listen("timeUpdateEvent")
  handleTimeUpdate(event: Event) {
    const args: VideoplayerProgressEventArgs = event["detail"];
    this.secondsPlayed = args.seconds;
    this.percentPlayed = args.percent;
  }

  @Listen("toggleCaptionsEvent")
  handleToggleCaptions() {
    this.captions = !this.captions;
  }

  @Watch("scheme")
  applyHeadlineScheme(): void {
    this.headlineComponent &&
      this.headlineComponent.setAttribute("scheme", this.activeScheme.color.replace("eon-", ""));
  }

  handleKeyPress(event: KeyboardEvent) {
    if (this.hideControls) {
      return;
    }

    const isMuteOrFullscreenButton =
      (event.target as HTMLElement).classList.contains("eonui-mute") ||
      (event.target as HTMLElement).classList.contains("eonui-fullscreen");

    if (event.key === KeyIds.SPACE || event.key === KeyIds.ENTER) {
      if (isMuteOrFullscreenButton) return;
      this.handlePlayToggle();
    }

    if (event.key === KeyIds.RIGHT || event.key === KeyIds.LEFT) {
      this.keyboardSeek(event);
    }
  }

  private keyboardSeek(event) {
    if (event.key === KeyIds.RIGHT) {
      this.seekTime = this.secondsPlayed + 5 <= this.duration ? this.secondsPlayed + 5 : this.duration;
    }

    if (event.key === KeyIds.LEFT) {
      this.seekTime = this.secondsPlayed - 5 > 0 ? this.secondsPlayed - 5 : 0;
    }
  }

  private handleClick(_event: MouseEvent) {
    if (this.hideControls) {
      return;
    }
    if (
      isSafari() &&
      (this.type === VIDEOPLAYER_TYPE_FILE || this.type === VIDEOPLAYER_TYPE_MOVING_IMAGE) &&
      this.isPlaying
    ) {
      return;
    }
    this.handlePlayToggle();
  }

  handleEnterViewport() {
    if (this.autoplay || this.isPausedOutOfViewport) {
      this.isPausedOutOfViewport && this.play();
      this.isPausedOutOfViewport = false;
    }
  }

  handleLeaveViewport() {
    if (this.isPlaying && !this.isFullscreen) {
      this.pause();
      this.isPausedOutOfViewport = true;
    }
  }

  private EnterViewport(element: HTMLElement) {
    if (window && window.IntersectionObserver) {
      const videoObserver = new window.IntersectionObserver(
        (entries: IntersectionObserverEntry[]) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              this.handleEnterViewport();
            } else {
              this.handleLeaveViewport();
            }
          });
        },
        {
          threshold: [0, 1]
        }
      );
      videoObserver.observe(element);
    }
  }

  imageAsPoster() {
    const image: HTMLEonUiLinkElement = getFirstNestedChild(this.hostElement, "eon-ui-image");
    if (image) {
      image.getAttribute("alt") ?? image.setAttribute("alt", this.currentLocalization.posterAltDefault);
      image.getAttribute("title") ?? image.setAttribute("title", this.currentLocalization.posterTitleDefault);
      image.setAttribute("use-as-background", "true");
      this.posterImagePlaceholder = true;
    } else {
      this.posterImagePlaceholder = false;
    }
  }

  render() {
    const canPlayButtonCoverYouTubeButton =
      this.type === VIDEOPLAYER_TYPE_YOUTUBE && !(this.posterImagePlaceholder && this.posterImage);
    const headlineInvisibleOnYoutube =
      this.type === VIDEOPLAYER_TYPE_YOUTUBE && (this.isPlaying || (!this.isPlaying && !this.isStopped));
    const isPlayButtonHidden = canPlayButtonCoverYouTubeButton && !this.headline;
    const isPlaceholderVisible = (this.isStopped && !this.autoplay && !isPlayButtonHidden) || this.hasError;
    const isHeadlineVisible =
      this.isStopped || !this.isPlaying || (this.isMouseOver && this.isPlaying && !this.hideControls);
    const filePlayerOnSafari =
      isSafari() && (this.type === VIDEOPLAYER_TYPE_FILE || this.type === VIDEOPLAYER_TYPE_MOVING_IMAGE);
    const isPlayButtonHiddenInSafari = filePlayerOnSafari && !(this.posterImagePlaceholder && this.posterImage);

    return (
      <Host>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
        <div
          class={pfx({
            videoplayer: true,
            [`has-orientation-${this.orientation}`]: true,
            ["force-full-height"]: this.useAsBackground,
            ["is-touch"]: isTouchDevice()
          })}
          ref={(el) => (this.videoplayerElement = el)}
          tabindex={`${filePlayerOnSafari ? "1" : "-1"}`}
          onKeyDown={(e: KeyboardEvent) => this.handleKeyPress(e)}
        >
          <div
            class={pfx({
              relative: true,
              fullscreen: this.isFullscreen,
              ["force-full-height"]: this.useAsBackground
            })}
          >
            <div class={pfx("absolute")}>
              {
                /* The actual video player */
                <div
                  class={pfx({
                    player: true,
                    fullscreen: this.isFullscreen,
                    safari: isSafari(),
                    ["click-event"]: this.hideControls,
                    iOSYoutubeHack: this.type === VIDEOPLAYER_TYPE_YOUTUBE && this.isIosMobileDevice,
                    placeholder: this.posterImagePlaceholder && this.posterImage && isPlaceholderVisible,
                    playing: this.isPlaying
                  })}
                  role="presentation"
                >
                  {(this.type === VIDEOPLAYER_TYPE_FILE || this.type === VIDEOPLAYER_TYPE_MOVING_IMAGE) && (
                    <eon-ui-videoplayer-file
                      autoplay={this.autoplay}
                      is-muted={this.isMuted}
                      is-playing={this.isPlaying}
                      is-fullscreen={this.isFullscreen}
                      seek-time={this.seekTime}
                      source={this.src}
                      track={this.track}
                      currentLocalization={this.currentLocalization}
                      onClick={(e) => this.handleClick(e)}
                      type={this.type}
                      controls={isSafari() && !this.useAsBackground}
                      captions={this.captions}
                      languageCode={this.languageCode}
                    ></eon-ui-videoplayer-file>
                  )}
                  {this.type === VIDEOPLAYER_TYPE_YOUTUBE && (
                    <eon-ui-youtubeplayer
                      autoplay={this.autoplay}
                      is-muted={this.isMuted}
                      is-playing={this.isPlaying}
                      seek-time={this.seekTime}
                      source={this.src}
                      hide-controls={this.hideControls}
                      onClick={(e) => this.handleClick(e)}
                      no-cookie={this.noCookie}
                      language-code={this.languageCode}
                      captions={this.captions}
                    />
                  )}
                </div>
              }
              {
                /* Custom controls */
                !this.hasError &&
                  !this.hideControls &&
                  !filePlayerOnSafari &&
                  (!this.controlsBelow || this.isFullscreen) && (
                    <eon-ui-videoplayer-controls
                      duration={this.duration}
                      hide-mute-button={this.hideMuteButton}
                      hide-full-screen-button={this.isIosMobileDevice}
                      hide-captions-button={!this.track && this.type != VIDEOPLAYER_TYPE_YOUTUBE ? "true" : "false"}
                      captions={this.captions || this.type == VIDEOPLAYER_TYPE_YOUTUBE}
                      is-fullscreen={this.isFullscreen}
                      is-muted={this.isMuted}
                      is-playing={this.isPlaying}
                      percent-played={this.percentPlayed}
                      seconds-played={this.secondsPlayed}
                      currentLocalization={this.currentLocalization}
                    ></eon-ui-videoplayer-controls>
                  )
              }
              {
                /* Poster */
                isPlaceholderVisible && (
                  <div
                    class={pfx({
                      placeholder: true,
                      ["click-event"]: filePlayerOnSafari
                    })}
                  >
                    {this.posterImagePlaceholder && this.posterImage && (
                      /* Poster */
                      <slot name="image"></slot>
                    )}
                  </div>
                )
              }
              {
                /* Headline, Play button */
                !this.hasError && (
                  <div class={pfx("video-container")}>
                    <div class={pfx("content")}>
                      {this.headline && (
                        <div
                          class={pfx({
                            "content-headline": true,
                            ["invisible-in-small-grids"]:
                              canPlayButtonCoverYouTubeButton || headlineInvisibleOnYoutube || !isHeadlineVisible
                          })}
                        >
                          {
                            /* Headline */
                            <span class={pfx("headline")}>
                              <eon-ui-headline
                                ref={(ref) => {
                                  this.headlineComponent = ref;
                                }}
                                text={this.headline}
                                headlineHtmlTag={this.seoHeadlineTag}
                                textShadow={this.headlineTextShadow}
                                alignment={HEADLINE_ALIGNMENT_LEFT}
                                margin={false}
                              ></eon-ui-headline>
                            </span>
                          }
                        </div>
                      )}
                      <div class={pfx("content-button")}>
                        {
                          /* Play button */
                          !isPlayButtonHidden && isPlaceholderVisible && !isPlayButtonHiddenInSafari && (
                            <eon-ui-button
                              class={pfx({
                                centered: !!this.headline,
                                "invisible-in-small-grids": canPlayButtonCoverYouTubeButton,
                                "video-replay": this.hasFinishedPlaying
                              })}
                              // eslint-disable-next-line jsx-a11y/tabindex-no-positive

                              scheme={VIDEOPLAYER_BUTTON_SCHEME}
                              icon={
                                this.hasFinishedPlaying
                                  ? "video_replay"
                                  : VIDEOPLAYER_FILLED_ICON
                                  ? "play_filled"
                                  : "play"
                              }
                              onClick={(_e) => this.handlePlay()}
                              aria-label={
                                this.hasFinishedPlaying
                                  ? this.currentLocalization.replay
                                  : this.currentLocalization.play
                              }
                              title={
                                this.hasFinishedPlaying
                                  ? this.currentLocalization.replay
                                  : this.currentLocalization.play
                              }
                            ></eon-ui-button>
                          )
                        }
                      </div>
                    </div>
                  </div>
                )
              }
              {
                /* Error */
                this.hasError && this.showErrorMessage && (
                  <div class={pfx({ error: true, semitransparent: isPlaceholderVisible })}>
                    <div class={pfx("error-content")}>
                      <p class={pfx("error-text")}>{this.currentLocalization.unableToPlay}</p>
                    </div>
                  </div>
                )
              }
              {
                <div
                  role="button"
                  tabIndex={this.useAsBackground ? -1 : 0}
                  onClick={(_e) => {
                    this.handlePlayToggle();
                  }}
                  onKeyDown={(e: KeyboardEvent) => this.handleKeyPress(e)}
                  class={pfx({
                    ["video-wrapper"]: true,
                    ["start"]: this.isStopped,
                    ["playing"]: this.isPlaying,
                    [`pause${this.type === VIDEOPLAYER_TYPE_YOUTUBE ? "-youtube" : ""}`]:
                      !this.isPlaying && !this.isStopped,
                    ["click-event"]: filePlayerOnSafari,
                    ["shadow-overlay"]: this.shadowOnVideo,
                    ["background-video"]: this.useAsBackground
                  })}
                  aria-label={this.a11yAriaLabel}
                  aria-describedby={this.videoButtonDescriptionId}
                  aria-roledescription={this.currentLocalization.videoButtonRoledescription}
                >
                  <div class={pfx("videobutton-description")} id={this.videoButtonDescriptionId}>
                    {this.currentLocalization.videoButtonDescription}
                  </div>
                </div>
              }
            </div>
          </div>
          {
            /* Custom controls */
            !this.hasError && !this.hideControls && !filePlayerOnSafari && this.controlsBelow && !this.isFullscreen && (
              <div
                class={pfx({
                  videoplayer: true,
                  "bottom-controls": true
                })}
              >
                <eon-ui-videoplayer-controls
                  duration={this.duration}
                  hide-mute-button={this.hideMuteButton}
                  hide-full-screen-button={this.isIosMobileDevice}
                  is-fullscreen={this.isFullscreen}
                  is-muted={this.isMuted}
                  is-playing={this.isPlaying}
                  percent-played={this.percentPlayed}
                  seconds-played={this.secondsPlayed}
                  currentLocalization={this.currentLocalization}
                  captions={this.captions || this.type == VIDEOPLAYER_TYPE_YOUTUBE}
                  hideCaptionsButton={!this.track && this.type != VIDEOPLAYER_TYPE_YOUTUBE}
                ></eon-ui-videoplayer-controls>
              </div>
            )
          }
        </div>
      </Host>
    );
  }
}
